/**
 * Revisi desain berbayar ke-4..6 (VONIS-26SEP-PAKET-K2 BUSINESS-16) — panel STAF di Detail PD, tidak pernah tampil
 * ke customer. CS membuka tagihan (alasan wajib, hanya bila mendesak) → CS mencatat pembayaran manual → Kepala
 * Cabang/Owner mengonfirmasi (pencatat ≠ pengonfirmasi). Revisi ke-7 dst tidak disediakan. Nominal dari master.
 * Semua pagar di backend; tombol hanya cermin.
 */
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { apiErrorMessage } from "@/lib/inventoryApi";
import { mintaTeks } from "@/lib/dialogAplikasi";
import { formatIDR, formatDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const KATA = { MENUNGGU_BAYAR: "Menunggu bayar", DIBAYAR: "Dibayar — menunggu konfirmasi", LUNAS: "Lunas", BATAL: "Batal" };

export default function TagihanRevisiPanel({ drId, roleCode }) {
  const [data, setData] = useState(null);
  const [sibuk, setSibuk] = useState(false);
  const isCs = roleCode === "CS";
  const bolehKonfirmasi = roleCode === "KEPALA_CABANG" || roleCode === "OWNER";

  const muat = useCallback(async () => {
    try { setData((await api.get(`/tagihan-revisi/dr/${drId}`)).data); }
    catch (e) { toast.error(apiErrorMessage(e, "Tagihan revisi gagal dimuat.")); setData({ items: [] }); }
  }, [drId]);
  useEffect(() => { muat(); }, [muat]);

  const jalan = async (fn, ok) => {
    setSibuk(true);
    try { await fn(); toast.success(ok); await muat(); }
    catch (e) { toast.error(apiErrorMessage(e, "Gagal.")); }
    finally { setSibuk(false); }
  };

  const buka = async () => {
    const alasan = await mintaTeks(`Alasan membuka tagihan revisi ke-${data.revisi_berikutnya} (hanya bila mendesak):`, { minPanjang: 5 });
    if (alasan) jalan(() => api.post(`/tagihan-revisi/dr/${drId}`, { alasan }), "Tagihan revisi dibuka.");
  };
  const bayar = async (t) => {
    const referensi = await mintaTeks("Referensi pembayaran (no. transfer / kuitansi tunai):", { minPanjang: 3 });
    if (!referensi) return;
    const metode = /tunai|cash/i.test(referensi) ? "TUNAI" : "TRANSFER";
    jalan(() => api.post(`/tagihan-revisi/${t.id}/bayar`, { metode, referensi }), "Pembayaran dicatat — menunggu konfirmasi Kepala Cabang.");
  };
  const batal = async (t) => {
    const alasan = await mintaTeks("Alasan membatalkan tagihan ini:", { minPanjang: 5 });
    if (alasan) jalan(() => api.post(`/tagihan-revisi/${t.id}/batal`, { alasan }), "Tagihan dibatalkan.");
  };

  if (!data) return null;
  const bisaBuka = isCs && data.revisi_berikutnya >= data.berbayar_mulai && data.revisi_berikutnya <= data.plafon
    && !(data.items || []).some((t) => t.revisi_ke === data.revisi_berikutnya && t.status !== "BATAL");
  return (
    <Card data-testid="tagihan-revisi-panel">
      <CardHeader><CardTitle className="text-base">Revisi berbayar (ke-4 s.d. ke-6)</CardTitle></CardHeader>
      <CardContent className="text-sm space-y-2">
        <p className="text-muted-foreground">
          Jatah revisi terpakai {data.jatah_terpakai} dari {data.plafon}. Revisi ke-4 s.d. ke-6 berbayar dan TIDAK
          ditawarkan ke customer kecuali mendesak; customer membayar dulu, lalu revisi boleh dikirim. Revisi ke-7 dan
          seterusnya tidak disediakan.
        </p>
        {(data.items || []).map((t) => (
          <div key={t.id} className="border-t pt-2 flex flex-wrap items-center justify-between gap-2">
            <span>
              Revisi ke-{t.revisi_ke} · {formatIDR(t.nominal_idr)} · <b>{KATA[t.status] || t.status}</b>
              <span className="block text-xs text-muted-foreground">
                {t.alasan}{t.referensi_bayar ? ` · ${t.metode_bayar} ${t.referensi_bayar}` : ""}{t.dicatat_pada ? ` · dicatat ${formatDateTime(t.dicatat_pada)}` : ""}
              </span>
            </span>
            <span className="flex gap-2">
              {isCs && t.status === "MENUNGGU_BAYAR" && (
                <Button size="sm" disabled={sibuk} onClick={() => bayar(t)} data-testid={`tagihan-revisi-bayar-${t.id}`}>Catat dibayar</Button>
              )}
              {bolehKonfirmasi && t.status === "DIBAYAR" && (
                <Button size="sm" disabled={sibuk} data-testid={`tagihan-revisi-konfirmasi-${t.id}`}
                        onClick={() => jalan(() => api.post(`/tagihan-revisi/${t.id}/konfirmasi`), "Pembayaran revisi dikonfirmasi LUNAS.")}>Konfirmasi lunas</Button>
              )}
              {(isCs || bolehKonfirmasi) && t.status === "MENUNGGU_BAYAR" && (
                <Button size="sm" variant="outline" disabled={sibuk} onClick={() => batal(t)}>Batalkan</Button>
              )}
            </span>
          </div>
        ))}
        {bisaBuka && (
          <Button size="sm" variant="outline" disabled={sibuk} onClick={buka} data-testid="tagihan-revisi-buka">
            Buka tagihan revisi ke-{data.revisi_berikutnya}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
